import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, MapPin, Edit, Save, X, BarChart3, TrendingUp, CheckCircle } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TelecallerService } from '../../services/telecallerService';

const TelecallerProfile = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
  });

  const { data: statsData } = useQuery({
    queryKey: ['telecallerStats', user?.id],
    queryFn: () => TelecallerService.getTelecallerStats(user?.id),
    enabled: !!user?.id,
  });

  const stats = statsData?.data || {};

  const handleSave = async () => {
    if (!formData.name || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      // Update via service if available, otherwise update localStorage
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('roadserve_current_user', JSON.stringify(updatedUser));
      queryClient.invalidateQueries(['telecallerProfile', user?.id]);
      alert('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      alert('Error updating profile: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/telecaller/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600 mt-1">Manage your profile information</p>
        </div>
      </div>

      {/* Profile Header */}
      <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl p-8 text-white">
        <div className="flex items-center space-x-6">
          <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-4xl">
              {user?.name?.charAt(0).toUpperCase() || 'T'}
            </span>
          </div>
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-2">{formData.name || 'Telecaller'}</h2>
            <p className="text-white/80 text-lg">{formData.email || 'No email'}</p>
            <p className="text-white/70 text-sm mt-1">Telecaller</p>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Personal Information</h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <Edit size={18} />
              <span>Edit</span>
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    name: user?.name || '',
                    email: user?.email || '',
                    phone: user?.phone || '',
                    address: user?.address || '',
                  });
                }}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <X size={18} />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                <Save size={18} />
                <span>{isSaving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            ) : (
              <div className="flex items-center space-x-3">
                <User size={20} className="text-gray-400" />
                <span className="text-gray-900">{formData.name || 'N/A'}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <div className="flex items-center space-x-3">
              <Mail size={20} className="text-gray-400" />
              <span className="text-gray-900">{formData.email || 'N/A'}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            ) : (
              <div className="flex items-center space-x-3">
                <Phone size={20} className="text-gray-400" />
                <span className="text-gray-900">{formData.phone || 'N/A'}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            {isEditing ? (
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            ) : (
              <div className="flex items-start space-x-3">
                <MapPin size={20} className="text-gray-400 mt-1" />
                <span className="text-gray-900">{formData.address || 'N/A'}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Performance Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Performance Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <BarChart3 size={24} className="mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.assignedLeads || 0}</p>
            <p className="text-sm text-gray-600">Assigned Leads</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <CheckCircle size={24} className="mx-auto text-green-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.confirmed || 0}</p>
            <p className="text-sm text-gray-600">Confirmed</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <TrendingUp size={24} className="mx-auto text-purple-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.booked || 0}</p>
            <p className="text-sm text-gray-600">Booked</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <BarChart3 size={24} className="mx-auto text-yellow-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.conversionRate || 0}%</p>
            <p className="text-sm text-gray-600">Conversion</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelecallerProfile;

